"use client";

import { useState, useCallback, useRef, useEffect } from "react";

const DEFAULT_FILTERS = {
	sort: "newest",
	minPrice: "",
	maxPrice: "",
	category: "",
	search: "",
	availability: "all",
	onlyDiscounted: false,
};

export function useProductListing(baseParams = {}, initialData = null) {
	const [state, setState] = useState(() =>
		initialData
			? {
					products: initialData.products,
					totalCount: initialData.totalCount,
					currentPage: initialData.currentPage,
					totalPages: initialData.totalPages,
					loading: false,
					loadingMore: false,
				}
			: {
					products: [],
					totalCount: 0,
					currentPage: 1,
					totalPages: 0,
					loading: true,
					loadingMore: false,
				},
	);

	const [filters, setFilters] = useState({ ...DEFAULT_FILTERS, ...baseParams });

	const fetchController = useRef(null);
	const debounceTimer = useRef(null);
	const baseParamsRef = useRef(baseParams);
	baseParamsRef.current = baseParams;

	const fetchProducts = useCallback(async (page = 1, currentFilters = filters, { append = false } = {}) => {
		if (fetchController.current) {
			fetchController.current.abort();
		}
		fetchController.current = new AbortController();

		setState((prev) => ({ ...prev, loading: !append, loadingMore: append }));

		try {
			const activeFilters = Object.fromEntries(
				Object.entries(currentFilters).filter(([, v]) => v !== "" && v !== false && v !== "all"),
			);

			const params = new URLSearchParams({ page, limit: 32, ...activeFilters, ...baseParamsRef.current });

			const res = await fetch(`/api/products?${params}`, {
				signal: fetchController.current.signal,
			});

			if (!res.ok) throw new Error(`HTTP ${res.status}`);

			const data = await res.json();

			setState((prev) => ({
				products: append ? [...prev.products, ...data.products] : data.products,
				totalCount: data.totalCount,
				currentPage: data.currentPage,
				totalPages: data.totalPages,
				loading: false,
				loadingMore: false,
			}));
		} catch (err) {
			if (err.name === "AbortError") return;
			console.error("Error loading products:", err);
			setState((prev) => ({ ...prev, loading: false, loadingMore: false }));
		}
	}, []);

	const handleFilterChange = useCallback(
		(newFilters) => {
			setFilters((prev) => {
				const updated = typeof newFilters === "function" ? newFilters(prev) : { ...prev, ...newFilters };

				clearTimeout(debounceTimer.current);
				debounceTimer.current = setTimeout(
					() => fetchProducts(1, updated),
					updated.search !== prev.search ? 400 : 0,
				);

				return updated;
			});
		},
		[fetchProducts],
	);

	useEffect(() => {
		if (initialData) return;
		fetchProducts(1, { ...DEFAULT_FILTERS, ...baseParamsRef.current });
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);

	const handlePageChange = useCallback(
		(newPage) => {
			fetchProducts(newPage, filters);
			window.scrollTo({ top: 0, behavior: "smooth" });
		},
		[filters, fetchProducts],
	);

	const handleLoadMore = useCallback(() => {
		if (state.loading || state.loadingMore || state.currentPage >= state.totalPages) return;
		fetchProducts(state.currentPage + 1, filters, { append: true });
	}, [state.loading, state.loadingMore, state.currentPage, state.totalPages, filters, fetchProducts]);

	return { state, filters, handleFilterChange, handlePageChange, handleLoadMore };
}
